/** @jsxImportSource react */
import { cx } from '@common/lib/clsxm';
import clsxm from '@common/lib/clsxm';
import { Calendar } from '@sly/icons/react';
import { Chevron } from '@sly/icons/react';
import dayjs from 'dayjs';
import { bool } from 'proptypes';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

const DatePicker = forwardRef(
  (
    {
      hideCalendarIcon = false,
      borderless = false,
      datePickerProps = {},
      value,
      selected: _,
      onChange,
      filterDate,
      yearNavigation = false,
      maxDate,
      minDate,
      ...props
    },
    ref
  ) => {
    const [date, setDate] = useState(() => (value ? new Date(value) : null));

    const maxYear = maxDate?.getFullYear() ?? new Date().getFullYear();
    const minYear = minDate?.getFullYear() ?? maxYear - 120;

    const yearOptions = useMemo(
      () =>
        Array.from(
          { length: maxYear - minYear + 1 },
          (_, index) => maxYear - index
        ),
      [maxYear, minYear]
    );

    const chevronClasses = yearNavigation ? 'h-3 w-3' : 'h-5 w-5';

    const handleChange = (date) => {
      setDate(date);
      const formatted = dayjs(date).format('MM/DD/YYYY');
      if (onChange) {
        onChange(formatted);
      }
    };

    const getFilter = useCallback(
      (day) => {
        if (filterDate) {
          return filterDate(day);
        }
        return true;
      },
      [filterDate]
    );

    useEffect(() => {
      const newValue = value ? new Date(value).getTime() : null;
      const currentDate = date ? date.getTime() : null;

      if (newValue !== currentDate) {
        setDate(newValue ? new Date(newValue) : null);
      }
    }, [date, value]);

    return (
      <>
        <div
          className={cx(
            'formDatePicker flex items-center bg-white',
            borderless ? 'border-none' : 'rounded border'
          )}
        >
          {!hideCalendarIcon && (
            <div className='pl-4'>
              <Calendar size='s' color='viridian.base' />
            </div>
          )}
          <ReactDatePicker
            ref={ref}
            autoComplete='off'
            {...props}
            {...datePickerProps}
            selected={date}
            onChange={handleChange}
            filterDate={getFilter}
            maxDate={maxDate}
            minDate={minDate}
            renderCustomHeader={({
              date,
              decreaseMonth,
              increaseMonth,
              changeYear,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
              prevYearButtonDisabled,
              nextYearButtonDisabled,
            }) => (
              <div className='flex items-center justify-between px-2 py-2'>
                {!yearNavigation && (
                  <span className='font-b-l text-slate-lighter-20'>
                    {dayjs(date).format('MMMM, YYYY')}
                  </span>
                )}
                {yearNavigation && (
                  <div className='space-x-2'>
                    <button
                      onClick={() => changeYear(dayjs(date).year() - 1)}
                      disabled={prevYearButtonDisabled}
                      type='button'
                      className={clsxm(
                        prevYearButtonDisabled &&
                          'cursor-not-allowed opacity-50',
                        'inline-flex rounded border border-slate-lighter-90 bg-white p-1 text-sm font-medium shadow-sm hover:bg-gray-50'
                      )}
                    >
                      <Chevron
                        className={`${chevronClasses} -rotate-90 text-viridian-base`}
                        size='xs'
                      />
                    </button>
                    <select
                      aria-label='Select year'
                      className='font-b-xs bg-transparent text-slate-base'
                      value={dayjs(date).year()}
                      onChange={(e) => changeYear(Number(e.target.value))}
                    >
                      {yearOptions.map((optionYear) => (
                        <option key={optionYear} value={optionYear}>
                          {optionYear}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => changeYear(dayjs(date).year() + 1)}
                      disabled={nextYearButtonDisabled}
                      type='button'
                      id='next-year'
                      className={clsxm(
                        nextYearButtonDisabled &&
                          'cursor-not-allowed opacity-50',
                        'inline-flex rounded border border-slate-lighter-90 bg-white p-1 text-sm font-medium shadow-sm hover:bg-gray-50'
                      )}
                    >
                      <Chevron
                        className={`${chevronClasses} rotate-90 text-viridian-base`}
                        size='xs'
                      />
                    </button>
                  </div>
                )}

                <div className='space-x-2'>
                  <button
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    type='button'
                    className={clsxm(
                      prevMonthButtonDisabled &&
                        'cursor-not-allowed opacity-50',
                      'inline-flex rounded border border-slate-lighter-90 bg-white p-1 text-sm font-medium shadow-sm hover:bg-gray-50'
                    )}
                  >
                    <Chevron
                      className={`${chevronClasses} -rotate-90 text-viridian-base`}
                      size='xs'
                    />
                  </button>
                  {yearNavigation && (
                    <span className='font-b-xs text-slate-lighter-20'>
                      {dayjs(date).format('MMMM')}
                    </span>
                  )}

                  <button
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    type='button'
                    id='next-month'
                    className={clsxm(
                      nextMonthButtonDisabled &&
                        'cursor-not-allowed opacity-50',
                      'inline-flex rounded border border-slate-lighter-90 bg-white p-1 text-sm font-medium shadow-sm hover:bg-gray-50'
                    )}
                  >
                    <Chevron
                      className={`${chevronClasses} rotate-90 text-viridian-base`}
                      size='xs'
                    />
                  </button>
                </div>
              </div>
            )}
          />
        </div>
      </>
    );
  }
);

DatePicker.propTypes = {
  hideCalendarIcon: bool,
  borderless: bool,
};

export default DatePicker;
